import { Temporal } from "@js-temporal/polyfill";
import { PLANNING_TIMEZONE } from "./constants.js";
import { isValidIsoDay } from "./day.js";
import { isValidHhmm } from "./hhmm.js";
/**
 * Given a `YYYY-MM-DD` Berlin calendar day, returns the UTC boundaries
 * for that day as `{ start, endExclusive }` — i.e. midnight Berlin to
 * midnight of the following day, both as UTC `Date` objects.
 *
 * DST-aware: the span may be 23 h (spring-forward) or 25 h (fall-back)
 * instead of the usual 24.
 */
export function isoDayToBerlinDayBounds(day) {
    if (!isValidIsoDay(day)) {
        throw new Error(`Expected YYYY-MM-DD day, got: "${day}"`);
    }
    const pd = Temporal.PlainDate.from(day);
    const midnight = new Temporal.PlainTime();
    const startZdt = pd.toPlainDateTime(midnight).toZonedDateTime(PLANNING_TIMEZONE);
    const endZdt = pd.add({ days: 1 }).toPlainDateTime(midnight).toZonedDateTime(PLANNING_TIMEZONE);
    return {
        start: new Date(startZdt.epochMilliseconds),
        endExclusive: new Date(endZdt.epochMilliseconds),
    };
}
/**
 * Extract the `YYYY-MM-DD` Berlin calendar day from a UTC instant.
 *
 * @example dateToBerlinIsoDay(new Date("2026-01-15T23:30:00Z")) // "2026-01-16"
 */
export function dateToBerlinIsoDay(date) {
    const instant = Temporal.Instant.fromEpochMilliseconds(date.getTime());
    const zdt = instant.toZonedDateTimeISO(PLANNING_TIMEZONE);
    return zdt.toPlainDate().toString();
}
/**
 * Compare two instants for the same Berlin calendar day.
 */
export function isSameBerlinDay(a, b) {
    return dateToBerlinIsoDay(a) === dateToBerlinIsoDay(b);
}
/**
 * Extract the Berlin wall-clock hour and minute from a UTC instant.
 * Useful for positioning calculations (e.g. Gantt chart time indicator).
 */
export function dateToBerlinHourMinute(date) {
    const instant = Temporal.Instant.fromEpochMilliseconds(date.getTime());
    const zdt = instant.toZonedDateTimeISO(PLANNING_TIMEZONE);
    return { hour: zdt.hour, minute: zdt.minute };
}
/**
 * Combine a `YYYY-MM-DD` day and an `HH:mm` time into an RFC 3339 string with
 * the correct Berlin offset for that instant (handles DST).
 */
export function composeBerlinRfc3339(day, hhmm) {
    if (!isValidIsoDay(day)) {
        throw new Error(`Expected YYYY-MM-DD day, got: "${day}"`);
    }
    if (!isValidHhmm(hhmm)) {
        throw new Error(`Expected HH:mm time, got: "${hhmm}"`);
    }
    const plainDt = Temporal.PlainDateTime.from(`${day}T${hhmm}:00`);
    const zdt = plainDt.toZonedDateTime(PLANNING_TIMEZONE);
    return zdt.toString({ timeZoneName: "never" });
}
/**
 * Combine a `YYYY-MM-DD` day and an `HH:mm` Berlin wall-clock time into a UTC
 * ISO string (`…Z`, with millisecond precision). Use this when a downstream
 * accepts only the UTC form (e.g. Zod's default `z.string().datetime()`),
 * while you still want to express the moment in Berlin wall-clock terms.
 */
export function composeBerlinUtcIso(day, hhmm) {
    if (!isValidIsoDay(day)) {
        throw new Error(`Expected YYYY-MM-DD day, got: "${day}"`);
    }
    if (!isValidHhmm(hhmm)) {
        throw new Error(`Expected HH:mm time, got: "${hhmm}"`);
    }
    const plainDt = Temporal.PlainDateTime.from(`${day}T${hhmm}:00`);
    const zdt = plainDt.toZonedDateTime(PLANNING_TIMEZONE);
    return new Date(zdt.epochMilliseconds).toISOString();
}
/**
 * Convert a `Date` to an RFC 3339 string in Berlin wall-clock time with the
 * correct offset (CET `+01:00` or CEST `+02:00`).
 *
 * Produces the same format as `composeBerlinRfc3339`, making the two directly
 * comparable for equality checks.
 */
export function dateToBerlinRfc3339(date) {
    const instant = Temporal.Instant.fromEpochMilliseconds(date.getTime());
    const zdt = instant.toZonedDateTimeISO(PLANNING_TIMEZONE);
    return zdt.toString({ timeZoneName: "never" });
}
